import { generatePath } from "react-router-dom";
import { routes } from "../routes";

/**
 * A project id that can be put in a URL and sent to the API.
 * A space is truthy, and react-router encodes it as %20, so `id || " "`
 * becomes /traffic-trend/%20/. That path is not a project.
 */
export function usableProjectId(value: string | null | undefined): string | null {
  if (typeof value !== "string") {
    return null;
  }
  const id = value.trim();
  if (id === "") {
    return null;
  }
  try {
    // "%20" (and other encoded whitespace) is still a blank id.
    if (decodeURIComponent(id).trim() === "") {
      return null;
    }
  } catch {
    // The value is not percent-encoded. The trimmed id can still be used.
  }
  return id;
}

/** The route captured a project id, and that id is blank (for example %20). */
export function isBlankProjectParam(projectId: string | undefined): boolean {
  return projectId !== undefined && usableProjectId(projectId) === null;
}

/** Build a project route, or the dashboard when the id must not be requested. */
export function pathWithProject(
  path: string,
  projectId: string | null | undefined,
  params: Record<string, string> = {}
): string {
  const id = usableProjectId(projectId);
  if (!id) {
    return routes.dashboard.path;
  }
  return generatePath(path, { ...params, projectId: id });
}

function pathSegments(path: string): string[] {
  const withoutQuery = path.split("?")[0].split("#")[0];
  return withoutQuery.split("/").filter((segment) => segment !== "");
}

function decodePathSegment(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

/**
 * Project id captured by an app route, such as a content check detail URL.
 * Pages without :projectId, including the dashboard, return null.
 */
export function projectIdFromPathname(
  pathname: string,
  routePaths: string[] = Object.values(routes).map((route) => route.path)
): string | null {
  const parts = pathSegments(pathname);
  let matched: string | null = null;
  let matchedLength = -1;
  for (const template of routePaths) {
    if (!template.includes(":projectId")) {
      continue;
    }
    const templateParts = pathSegments(template);
    if (templateParts.length !== parts.length || templateParts.length < matchedLength) {
      continue;
    }
    let projectId: string | null = null;
    let matches = true;
    for (let index = 0; index < templateParts.length; index += 1) {
      const token = templateParts[index];
      if (token.startsWith(":")) {
        if (token === ":projectId") {
          projectId = decodePathSegment(parts[index]);
        }
        continue;
      }
      if (token !== parts[index]) {
        matches = false;
        break;
      }
    }
    if (!matches || projectId === null) {
      continue;
    }
    matched = projectId;
    matchedLength = templateParts.length;
  }
  return usableProjectId(matched);
}

/**
 * True when any path segment is empty or whitespace.
 * `/projects/%20/traffic-trend` and `/projects/ /content-decay` both match.
 * A trailing slash does not.
 */
export function urlHasBlankPathSegment(url: string): boolean {
  const path = (url || "").split("?")[0].split("#")[0];
  let decoded = path;
  try {
    decoded = decodeURIComponent(path);
  } catch {
    decoded = path;
  }
  const segments = decoded.split("/");
  return segments.some((segment, index) => {
    if (index === 0) {
      return false;
    }
    // A trailing slash is an empty last segment, not a missing project id.
    if (index === segments.length - 1 && segment === "") {
      return false;
    }
    return segment.trim() === "";
  });
}
